import { z } from "zod";

/**
 * shareLinkTargetSchema is the resource a share link is minted, rotated, or disabled
 * for: an id and the KIND it belongs to. The kind is not a routing convenience — the
 * grant itself carries it (ADR 0028). An itinerary token is pure id-equality, reaching
 * exactly the one itinerary it was minted for, while a collection token reaches the
 * collection AND every itinerary and zone inside it, because exposing the set is the
 * entire purpose of a collection link. Naming the kind at the boundary means a caller
 * never has to infer which endpoint an id belongs to, and the two resources share one
 * dialog and one trio of tools.
 */
export const shareLinkTargetSchema = z.object({
  id: z.string(),
  kind: z.enum(["collection", "itinerary"]),
});

/**
 * ShareLinkTarget is the resource a share link is minted for, derived from
 * {@link shareLinkTargetSchema}.
 */
export type ShareLinkTarget = z.infer<typeof shareLinkTargetSchema>;

/**
 * ShareLinkInput is what a share link encodes: the shared resource and the
 * resource-scoped token that authorizes its read-only view. Exactly one target —
 * an itinerary or a collection — because a token is minted for one resource and
 * the surface has to know which one it was handed. A union rather than two
 * optional fields, so "both" and "neither" cannot be spelled at all.
 */
export type ShareLinkInput =
  | { collectionId: string; shareToken: string }
  | { itineraryId: string; shareToken: string };

/**
 * buildShareSearch turns a ShareLinkInput into the ordered [key, value] pairs the
 * /studio/share route reads through shareSearchSchema: the target id under its own
 * key, then the share token. Returned as pairs (not a joined string) so the caller
 * feeds them straight into a URLSearchParams — the same shape buildEmbedSearch
 * returns — which percent-encodes each value. Pure and render-free.
 */
export const buildShareSearch = (input: ShareLinkInput): Array<[string, string]> =>
  "collectionId" in input
    ? [
        ["collection_id", input.collectionId],
        ["share_token", input.shareToken],
      ]
    : [
        ["itinerary_id", input.itineraryId],
        ["share_token", input.shareToken],
      ];

/**
 * buildShareSrc composes the absolute share URL a visitor opens: the app origin, the
 * /studio/share path, and the encoded target id + share token. The token rides in
 * the URL because it IS the link a viewer follows; the surface then forwards it on
 * every read through the X-Share-Token header rather than as a query credential. Pure
 * so the share modal and its tests build the copyable link from one place.
 */
export const buildShareSrc = (origin: string, input: ShareLinkInput): string => {
  const params = new URLSearchParams(buildShareSearch(input));

  return `${origin}/studio/share?${params.toString()}`;
};
